"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import CustomNavLink from "@/components/CustomLink/CustomNavLink";
import {
  TextField,
  InputAdornment,
  Autocomplete,
  Box,
  Skeleton,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import usePost from "@/hooks/usePost";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import { useRouter } from "next/navigation";
import useImageUrlModifier from "@/lib/useImageUrlModifier";
type BrandType = {
  _id: string;
  brand: string;
  brandSlug: string;
  brandImg: string;
};
type BrandDataType = {
  data: BrandType[];
};

// Skeleton Loader Component
const SkeletonLoader = () => (
  <div className="w-full p-10 flex flex-col">
    {/* Header Skeleton */}
    <Box className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
      {/* Breadcrumb Skeleton */}
      <Skeleton variant="text" width={200} height={24} />
      {/* Search Bar Skeleton */}
      <Skeleton
        variant="rectangular"
        width="100%"
        height={44}
        sx={{ maxWidth: "18rem" }}
      />
    </Box>

    {/* Brands Grid Skeleton */}
    <Grid container spacing={2}>
      {Array.from({ length: 15 }).map((_, index) => (
        <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
          <Box className="w-full border rounded-lg overflow-hidden">
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ aspectRatio: "1" }}
            />
            <Skeleton
              variant="text"
              width="60%"
              sx={{ margin: "0.5rem auto" }}
            />
          </Box>
        </Grid>
      ))}
    </Grid>
  </div>
);

const Brands = ({ params }: { params: any }) => {
  const Router = useRouter();
  const { PostMethod, loading } = usePost();
  const [brandData, setBrandData] = useState<BrandDataType>();
  const [filteredBrands, setFilteredBrands] = useState<BrandType[]>();

  const modifyImageUrl = useImageUrlModifier();

  const dict = useDictionary(); // Use dictionary hook to get translations

  const fallbackImage = "/images/dummy-image-square.jpg"; // Fallback image

  const handleSearch = (_: any, value: string) => {
    const filtered = brandData?.data?.filter((brand: BrandType) =>
      brand?.brand?.toLowerCase()?.includes(value?.toLowerCase())
    );
    setFilteredBrands(filtered);
  };

  const getBrands = async () => {
    try {
      const res = await PostMethod({
        method: "get",
        url: "brand",
      });
      if (res?.success) {
        setBrandData(res);
        setFilteredBrands(res?.data);
        return;
      } else {
        toast.error(res?.message || "Something went Wrong");
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went Wrong");
    }
  };

  useEffect(() => {
    getBrands();

    // eslint-disable-next-line
  }, []);
  return loading ? (
    <SkeletonLoader />
  ) : (
    <div className="w-full p-4 sm:p-10 flex flex-col">
      {/* Page Header Section */}
      <div className="w-full flex flex-col sm:flex-row justify-start sm:justify-between items-start sm:items-center mb-6 gap-y-5 sm:gap-y-0">
        {/* Breadcrumb Navigation */}
        <div className="flex justify-start items-center gap-2 sm:text-lg">
          <div onClick={() => Router.back()} className="cursor-pointer">
            <span className="text-black/70 hover:text-black">
              {dict.headerText.home}
            </span>
          </div>
          <svg
            width="10"
            height="16"
            viewBox="0 0 10 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="rtl:rotate-180"
          >
            <path
              d="M9.5 8L2 15.5L0.950001 14.45L7.4 8L0.950001 1.55L2 0.5L9.5 8Z"
              fill="black"
              fill-opacity="0.6"
            />
          </svg>
          <span className="text-black cursor-pointer">
            {dict?.productListPage?.brandLower}
          </span>
        </div>

        {/* Search Bar */}
        <div className="w-full sm:w-72">
          <Autocomplete
            freeSolo
            options={
              brandData?.data.map((brand: BrandType) => brand.brand) || []
            }
            onInputChange={handleSearch}
            renderInput={(params: any) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={dict.Home.brandPlaceHolder}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: "#BABABA", marginLeft: "8px" }}
                      />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    maxHeight: "44px",
                    borderRadius: "6px",
                    outline: "2px solid #EEEEEE",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "1rem !important", // Adjust this as needed
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "1rem !important", // Adjust this as needed
                    padding: "0 !important",
                  },
                }}
              />
            )}
          />
        </div>
      </div>

      {/* Brands Grid */}
      <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filteredBrands?.map((item: BrandType, index: number) => (
          <div
            key={index}
            className="w-full group overflow-hidden border border-secondary rounded-lg"
          >
            <CustomNavLink
              href={`brands/${item?.brandSlug}`}
              prefetch={false}
              className="w-full aspect-square relative"
            >
              <Image
                src={
                  item?.brandImg.includes("https")
                    ? modifyImageUrl(item?.brandImg, 300, 300) || fallbackImage
                    : `${process.env.NEXT_PUBLIC_BASE}/${item?.brandImg}`.replace(/([^:]\/)\/+/g, '$1') ||
                      fallbackImage
                }
                className="rounded-lg object-contain group-hover:scale-105 transition-all duration-300"
                alt={`brand ${index}`}
                layout="responsive"
                width={1}
                height={1}
                placeholder="blur"
                blurDataURL={fallbackImage}
                priority={true}
              />

              <p className="w-full py-2 text-sm text-center md:text-base text-black font-semibold">
                {item?.brand}
              </p>
            </CustomNavLink>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Brands;
